import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, isEmpty, size } from 'lodash';
import Notification from 'common-ui-components/dist/components/Atoms/Notification';
import Header from '../../../containers/organisms/Header';
import Footer from '../../../containers/organisms/Footer';
import PasswordPrompt from '../../molecules/PasswordPrompt';
import {
  systemNotificationClose,
  signOutRequest,
  shortTermTokenExpired,
  userAuthorizationAction,
  userAuthorizationSuccess,
} from '../../../global/actions';
import {
  DISMISS_SYSTEM_NOTIFICATION_STORAGE,
  DISMISS_SYSTEM_NOTIFICATION_STORAGE_VALUE,
} from '../../../global/constants';
import getErrorMessage from '../../../utils/getErrorMessage';
import { sessionGetItemValue, sessionSetItemValue } from '../../../utils/storage';
import { getBoolValue, isEProcurementApp } from '../../../utils/utils';

import './Layout.scss';
import AnnouncementBanner from '../../molecules/AnnouncementBanner/AnnouncementBanner';
import { getStore } from '../../../utils/utilFunctions';
import SpecialAnnouncementBanner from '../../molecules/SpecialAnnouncementBanner/SpecialAnnouncementBanner';

const Banner = (allAnnouncements, isCheckout) =>
  !isEmpty(allAnnouncements) &&
  !isCheckout &&
  allAnnouncements.map((data, index) => (
    <AnnouncementBanner key={data} index={index} announcementData={data} />
  ));

// New function for Special Announcement
const SpecialBanner = (specialAnnouncementData, isCheckout, siteConfig) =>
  !isEmpty(specialAnnouncementData) && (
    <SpecialAnnouncementBanner announcementData={specialAnnouncementData} siteConfig={siteConfig} />
  );

// eslint-disable-next-line react/prop-types
const Layout = ({
  children,
  error,
  labels,
  config,
  isCheckout,
  pageContext,
  openSwitchAccountModal,
  isQuoteToOrder,
  isQuoteDisabled,
  showFaPiaoHistory,
  systemNotification,
  CloseSystemNotification,
  isCreateQuote,
  isConfirmation,
  pageContent,
  signOutAction,
  shortTermTokeExpiredData,
  onPasswordPromptClose,
  onUserAuthorizationAction,
  PasswordPromptData,
  clearPasswordPromptError,
  containerClass,
}) => {
  const errorMessage = getErrorMessage(error, labels);
  const { dismissButton } = labels;
  const { unavailableIconUrl, isSystemNotificationDisabled = false } = config;
  const { isSystemNotificationShow, notification = {} } = systemNotification;
  const { title, message } = notification;
  const disMissSystemNotification = sessionGetItemValue(DISMISS_SYSTEM_NOTIFICATION_STORAGE);
  const systemNotificationStatus = isEProcurementApp()
    ? false
    : getBoolValue(isSystemNotificationDisabled);
  const handleCloseSystemNotification = () => {
    sessionSetItemValue(
      DISMISS_SYSTEM_NOTIFICATION_STORAGE,
      DISMISS_SYSTEM_NOTIFICATION_STORAGE_VALUE
    );
    return CloseSystemNotification();
  };
  const store = getStore();
  const allAnnouncements = get(store, ['global', 'globalData', 'announcements'], {});
  const specialAnnouncementData = get(store, ['global', 'globalData', 'specialAnnouncement'], {});
  const siteConfigType = isEProcurementApp() ? 'eProcurement' : 'eCommerce';
  return (
    <div className="layout">
      <div className="top-container">
        {Banner(allAnnouncements, isCheckout)}
        <Header
          isCheckout={isCheckout}
          pageContext={pageContext}
          openSwitchAccountModal={openSwitchAccountModal}
          isQuoteToOrder={isQuoteToOrder}
          isQuoteDisabled={isQuoteDisabled}
          showFaPiaoHistory={showFaPiaoHistory}
          isCreateQuote={isCreateQuote}
          isConfirmation={isConfirmation}
        />
        {SpecialBanner(specialAnnouncementData, isCheckout, siteConfigType)}
        {isSystemNotificationShow &&
          !isEmpty(message) &&
          disMissSystemNotification !== DISMISS_SYSTEM_NOTIFICATION_STORAGE_VALUE &&
          !systemNotificationStatus && (
            <div className="container-sitewide-notification">
              <div className="container">
                <div className="cmp-notification--sitewide">
                  <Notification
                    className="col-xs-12 cmp-notification-body"
                    variation=""
                    type="system-notification"
                    title={title}
                    description={message}
                    linkText={dismissButton}
                    link={{ to: '#', onClick: () => handleCloseSystemNotification() }}
                    icon={unavailableIconUrl}
                    elementLocator="system-notification"
                  />
                </div>
              </div>
            </div>
          )}
      </div>

      <div className={`container ${containerClass}`}>
        {!isEmpty(errorMessage) &&
          size(errorMessage) > 0 &&
          errorMessage.map(item => {
            const { title: errorTitle, description } = item;
            return (
              (!isEmpty(errorTitle) || !isEmpty(description)) && (
                <Notification
                  className="col-xs-12"
                  variation="siteWide"
                  type="serverError"
                  title={errorTitle}
                  description={description}
                  icon={unavailableIconUrl}
                  elementLocator="notification-serverError"
                />
              )
            );
          })}
        {children}
      </div>
      {!isEmpty(shortTermTokeExpiredData) && (
        <PasswordPrompt
          labels={labels}
          config={config}
          pageContent={pageContent}
          signOutAction={signOutAction}
          shortTermTokeExpiredData={shortTermTokeExpiredData}
          onPasswordPromptClose={onPasswordPromptClose}
          onUserAuthorizationAction={onUserAuthorizationAction}
          PasswordPromptData={PasswordPromptData}
          clearPasswordPromptError={clearPasswordPromptError}
        />
      )}
      <Footer hideNavigation={isCheckout} />
    </div>
  );
};

Layout.propTypes = {
  error: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  labels: PropTypes.objectOf(PropTypes.string),
  config: PropTypes.objectOf(PropTypes.string),
  isCheckout: PropTypes.bool,
  children: PropTypes.node.isRequired,
  openSwitchAccountModal: PropTypes.func,
  pageContext: PropTypes.string,
  isQuoteToOrder: PropTypes.bool,
  isQuoteDisabled: PropTypes.bool,
  showFaPiaoHistory: PropTypes.bool,
  systemNotification: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool])
  ),
  CloseSystemNotification: PropTypes.func,
  isCreateQuote: PropTypes.bool,
  isConfirmation: PropTypes.bool,
  pageContent: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool])
  ),
  signOutAction: PropTypes.func,
  shortTermTokeExpiredData: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number])
  ),
  onPasswordPromptClose: PropTypes.func,
  onUserAuthorizationAction: PropTypes.func,
  PasswordPromptData: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  clearPasswordPromptError: PropTypes.func,
  containerClass: PropTypes.string,
};

Layout.defaultProps = {
  error: {},
  isCheckout: false,
  labels: {},
  config: {},
  shortTermTokeExpiredData: {},
  openSwitchAccountModal: () => {
    /* default openSwitchAccountModal handler */
  },
  CloseSystemNotification: () => {
    /* default CloseSystemNotification handler */
  },
  signOutAction: () => {
    /* default signOutAction handler */
  },
  onPasswordPromptClose: () => {
    /* default onPasswordPromptClose handler */
  },
  onUserAuthorizationAction: () => {
    /* default onUserAuthorizationAction handler */
  },
  clearPasswordPromptError: () => {
    /* default clearPasswordPromptError handler */
  },
  pageContext: '',
  isQuoteToOrder: false,
  isQuoteDisabled: false,
  showFaPiaoHistory: false,
  systemNotification: {},
  isCreateQuote: false,
  isConfirmation: false,
  pageContent: [],
  PasswordPromptData: {},
  containerClass: '',
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
  CloseSystemNotification: () => dispatch(systemNotificationClose()),
  signOutAction: data => dispatch(signOutRequest(data)),
  onPasswordPromptClose: data => dispatch(shortTermTokenExpired(data)),
  onUserAuthorizationAction: data => dispatch(userAuthorizationAction(data)),
  clearPasswordPromptError: data => dispatch(userAuthorizationSuccess(data)),
});

const mapStateToProps /* istanbul ignore next */ = state => ({
  error: get(state, ['global', 'globalData', 'error'], {}),
  labels: get(state, ['global', 'globalData', 'labels'], {}),
  config: get(state, ['global', 'globalData', 'config'], {}),
  systemNotification: get(state, ['global', 'globalData', 'systemNotification'], {}),
  pageContent: get(state, ['global', 'globalData', 'pageData', 'Page Content', '_children'], []),
  shortTermTokeExpiredData: get(state, ['global', 'globalData', 'shortTermToken'], {}),
  PasswordPromptData: get(state, ['global', 'globalData', 'PasswordPromptData'], {}),
});
export default connect(mapStateToProps, mapDispatchToProps)(Layout);
export { Layout, Banner };
